/**
 * Platform types supported by the app.
 */
export type AppPlatform = 'ios' | 'android' | 'web' | 'windows' | 'macos';

/**
 * User identity traits for feature flag targeting.
 * Used to evaluate flags based on user properties such as email, type, location, platform, and version.
 */
export type FeatureFlagTraits = {
  userId: string;
  email: string;
  userType: string;
  stateAbbreviation: string;
  platform: AppPlatform;
  appVersion: string;
};

/**
 * Centralized feature flag keys snake_case to match flag service conventions.
 * Each flag controls a specific feature's visibility or behavior.
 *
 * **Independent toggles:** do not merge in Flagsmith unless product wants a single switch.
 * - `INBOX_INQUIRY_GROUPS`: inbox list, grouped chat tabs, and related analytics context. Does not control anonymous fan-out on create.
 */
export const FEATURE_FLAGS = {
  AVENTRIA_LANDING_PAGE: 'aventria_landing_page',
  CHAT_DOT_PHRASES: 'chat_dot_phrases',
  /**
   * Release toggle for moving inquiry chat off Twilio. A string, not a boolean:
   * - `twilio` (default): chat runs on the Twilio Conversations SDK, as before.
   * - `shadow`: Twilio still runs the chat; the Primum transport reads the same
   *   conversations from our API in the background and reports where the two
   *   disagree, without showing anything different to the user.
   * - `primum`: chat runs on our own messages API and inbox realtime channel,
   *   with no Twilio client at all.
   * Any other value reads as `twilio`. Short-lived by design: once `primum` has
   * held for everyone, delete this flag together with the Twilio session.
   */
  CHAT_TRANSPORT: 'chat_transport',
  DEBUG_CHAT_MESSAGES: 'debug_chat_messages',
  DIRECTORY_CANCER_TYPE_COMBOBOX: 'directory_cancer_type_combobox',
  /**
   * The expert Availability row and time away sheet on the Account screen, with the away
   * banner naming the last day. They replace the old Active/Away switch. Off keeps that
   * switch and the original banner, and makes no away-schedule requests.
   */
  EXPERT_AWAY_CALENDAR: 'expert_away_calendar',
  /**
   * Surfaces the Reference Champion badge for experts who reference a high share of
   * their inquiries, across the directory cards, the profile and the inquiry sidebar.
   *
   * Gates the badge and nothing else. It briefly also gated the response-time restyle
   * and the card footer that the badge sits in, which meant the badge could not be
   * turned on without shipping a layout change to every surface that renders a response
   * time. Those are unconditional now, so this flag answers one question.
   *
   * Both halves live on this one flag: enabled gates the feature, and the value carries
   * the roster as JSON mapping expert id to on or off, so who holds the badge can be
   * retuned without a deploy. Qualification is measured offline and the payload carries
   * only the answer, so the roster is a snapshot that goes stale as experts keep
   * publishing.
   *
   *     { "836": true, "933": true }
   *
   * The value may instead carry operator-owned copy alongside the roster:
   *
   *     { "label": "Reference Champion", "emoji": "🏆", "roster": { "836": true } }
   *
   * Both copy fields are optional, and absence means different things. A missing name falls
   * back to the one in code, since a badge with nothing to say cannot render. A missing mark
   * means no mark: code holds no default glyph, so omitting the key is how the mark is
   * turned off, and no environment inherits a design decision it never asked for.
   *
   * A number is still read as on, since the environments predate the switch and hold
   * rate-shaped rosters. Only an explicit `false` turns an entry off, and it outranks the
   * `*` wildcard that badges everyone a roster does not name.
   *
   * There is deliberately no roster in code, so no value set means no badge renders
   * anywhere. A hardcoded default would badge whoever happens to hold that id in another
   * environment. An explicit `{}` means nobody qualifies, which switches every badge off
   * without disabling the flag.
   */
  EXPERT_REFERENCE_BADGES: 'expert_reference_badges',
  EXPERT_SELECT_SURGEON_GROUPING: 'expert_select_surgeon_grouping',
  /**
   * Gates Fellows Corner: the `/fellows` nav link and the promo banner. Off by
   * default; enable per environment or cohort to roll out. Controls eligibility
   * only; per-user banner dismissal is handled separately.
   *
   * The flag's *value* additionally carries the mentor questions, as a JSON array
   * of `{ mentorId, question, answer }`, so they can change without an app
   * release. Anything unset or malformed falls back to the in-repo set, which is
   * why the default stays `false` rather than a JSON string: it keeps the gate
   * off and fails the content parse in one value.
   */
  FELLOWS_CORNER: 'fellows_corner',
  INBOX_ANNOUNCEMENT_BANNER: 'inbox_announcement_banner',
  /**
   * Nests an inquiry group's expert relay threads under the original in the inbox,
   * for Primum staff only. This is the only switch for the cards and the
   * server-grouped read. It is not enough on its own: the reader must also be in the
   * Primum Cognito group, so turning it on never changes what an expert or a
   * submitter sees.
   */
  INBOX_INQUIRY_GROUP_CARDS: 'inbox_inquiry_group_cards',
  /** When on, inbox groups related threads and enables grouped chat UX. Does not control anonymous fan-out on create. */
  INBOX_INQUIRY_GROUPS: 'inbox_inquiry_groups',
  /**
   * Gates the desktop inbox right sidebar (third pane) and its chat-header
   * toggle. Shell only for now — the panel renders an empty placeholder until a
   * feature branch fills it, so this stays off in all environments. Kept wired
   * so the three-pane layout has a home and the toggle/open-close behavior can
   * be exercised behind the flag.
   */
  INBOX_SIDEBAR: 'inbox_sidebar',
  INQUIRY_FULLSCREEN_CONVERSATION: 'inquiry_fullscreen_conversation',
  INQUIRY_LABELS: 'inquiry_labels',
  /**
   * Surfaces the "Leave feedback" action in the inquiry action bar above the
   * composer (alongside On your honor). Scaffold only for now — the action is a
   * placeholder until the feedback flow is built, so this stays off in all
   * environments. Kept wired so the second inquiry action has a home and the
   * bar's multi-action layout can be exercised behind the flag.
   */
  INQUIRY_LEAVE_FEEDBACK: 'inquiry_leave_feedback',
  INQUIRY_MULTI_EXPERT_NOTES: 'inquiry_multi_expert_notes',
  /**
   * Shows the expert's institution's trials as a tab of the profile's Clinical
   * Trials section: trials listed at the institution in the expert's focus
   * areas, listed per organization rather than attributed to the expert. Off by
   * default so the profile reads as it did before; the tab also stays hidden
   * for an expert whose institution has none.
   */
  INSTITUTIONAL_TRIALS: 'institutional_trials',
  /**
   * Surfaces the "On your honor" extra-effort toggle in the inquiry action bar
   * above the composer (expert-only). Gates the toggle and its one-time intro
   * banner together; off by default, enable per environment / cohort to roll out.
   */
  INQUIRY_ON_YOUR_HONOR: 'inquiry_on_your_honor',
  /**
   * Gates the post-mark reason micro-survey independently of the toggle above:
   * with the toggle flag on but this off, marking still works but no reason
   * sheet opens. Lets the survey roll out to a monitored subset while the
   * toggle is live for a wider cohort. Off by default.
   */
  INQUIRY_ON_YOUR_HONOR_REASON: 'inquiry_on_your_honor_reason',
  /**
   * Shows the generated multi-expert summary card above an Inquiry Library
   * post. Off means the post reads exactly as it did before the card existed.
   *
   * A review switch rather than an entitlement: the endpoint already strips
   * the summary for logged-out readers, so the gate is here, where it can be
   * turned back on without a deploy. Move it server-side if it ever becomes a
   * question of who may see the data rather than of what renders.
   *
   * It gates the whole card, including the provenance note and the thumbs, so
   * a generated summary never shows without the note saying so.
   */
  INQUIRY_SUMMARY: 'inquiry_summary',
  /**
   * Shows an Inquiry Library post as its answers, one block per specialist under the
   * question, in place of its body. Off means the post reads exactly as it did.
   *
   * On is not enough on its own. A post renders as answers only when the API sent
   * its structured `question` and non-empty `answers`; any post without them keeps its body,
   * so turning this on early cannot blank a post the backfill has not reached.
   * Independent of INQUIRY_SUMMARY, which gates the card above the post.
   */
  INQUIRY_LIBRARY_ANSWERS: 'inquiry_library_answers',
  /**
   * Opens Research, the saved, multi-turn conversation that finds answered inquiries
   * related to a question in the clinician's own words. The route also requires the backend's
   * `AskConversation` ability, so the flag widens exposure but never grants access.
   */
  LIBRARY_CHAT: 'library_chat',
  MAINTENANCE_MODE: 'maintenance_mode',
  MEDICAL_ON_DEMAND: 'medical_on_demand',
  MICRO_SURVEYS: 'micro_surveys',
  MOD_DIRECT_TO_MSL: 'mod_direct_to_msl',
  /**
   * Gates J&J MIL as a third Medical On Demand route and the routing-section
   * redesign that holds it.
   *
   * One flag for both because they are one change: the redesign exists to hold
   * three options, and a third option needs a layout that fits it.
   *
   * Off by default, and it must stay off in production until the routing copy
   * clears CAC and the API accepts the new route, which it does not yet.
   * Enabling it early gives users a checkbox that fails validation. Sits under
   * the flag that gates the routing section existing at all.
   */
  MOD_MIL_ROUTE: 'mod_mil_route',
  MOD_PRODUCT_FEATURED: 'mod_product_featured',
  MOD_PRODUCT_SUPPORT_SUCCESS_SCREEN: 'mod_product_support_success_screen',
  /**
   * Gates the when-to-ask explainer: the "New" marker, the "When to ask J&J
   * Medical" link and the modal behind them, on the Treatment Support section
   * and on the card. Off by default, so it reaches nobody until it is turned
   * on per environment for the MOD promotional email, and turning it back off
   * retires all three together without a deploy.
   *
   * Both halves of the feature ride this one flag, because they are the same
   * feature. `enabled` is the gate above. `value` optionally carries the
   * explainer's copy as JSON, so the headings, comparison rows and worked
   * examples can change without an app release.
   *
   * Every copy field is optional and falls back to `WHEN_TO_USE_EXPLAINER`
   * field by field, so setting one line does not require restating the rest
   * and a malformed value degrades to the approved copy rather than to blanks.
   * That is the difference from Fellows Corner, which has no in-repo set and
   * so degrades to nothing.
   *
   * NOTE FOR WHOEVER SETS THIS. The in-repo copy is pinned by
   * `WhenToUseExplainer.test.tsx` because it is Vicki's and because a draft
   * transcribed from a screenshot once put a real J&J study in front of
   * clinicians. A flag value is a way around that pin, so copy authored here
   * needs the same approval the pinned copy has, and ideally lands back in the
   * constants once it settles.
   */
  MOD_WHEN_TO_ASK: 'mod_when_to_ask',
  NEW_QUESTION_AFTER_SIGNUP_FUNNEL: 'new_question_after_signup_funnel',
  /**
   * Surfaces a "Preview onboarding survey" entry in Account settings that opens
   * the hardcoded onboarding survey (EXPO_PUBLIC_ONBOARDING_SURVEY_GROUP_ID) with the
   * already-responded gate bypassed, so the form always renders even for a user
   * who has already responded. Submission still works normally — since the API
   * has no way to update an existing response, submitting again from preview
   * after an existing response creates an additional response row.
   */
  ONBOARDING_SURVEY_PREVIEW: 'onboarding_survey_preview',
} as const;

export type FeatureFlag = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

/**
 * Default flag values used when flag provider is unavailable for fail-safe behavior.
 */
export const FEATURE_FLAG_DEFAULTS: Record<FeatureFlag, boolean | string> = {
  [FEATURE_FLAGS.AVENTRIA_LANDING_PAGE]: 'directory',
  [FEATURE_FLAGS.CHAT_DOT_PHRASES]: false,
  [FEATURE_FLAGS.CHAT_TRANSPORT]: 'twilio',
  [FEATURE_FLAGS.DEBUG_CHAT_MESSAGES]: false,
  [FEATURE_FLAGS.DIRECTORY_CANCER_TYPE_COMBOBOX]: false,
  [FEATURE_FLAGS.EXPERT_AWAY_CALENDAR]: false,
  [FEATURE_FLAGS.EXPERT_REFERENCE_BADGES]: false,
  [FEATURE_FLAGS.EXPERT_SELECT_SURGEON_GROUPING]: false,
  [FEATURE_FLAGS.FELLOWS_CORNER]: false,
  [FEATURE_FLAGS.INBOX_ANNOUNCEMENT_BANNER]: false,
  [FEATURE_FLAGS.INBOX_INQUIRY_GROUPS]: false,
  [FEATURE_FLAGS.INBOX_INQUIRY_GROUP_CARDS]: false,
  [FEATURE_FLAGS.INBOX_SIDEBAR]: false,
  [FEATURE_FLAGS.INQUIRY_FULLSCREEN_CONVERSATION]: false,
  [FEATURE_FLAGS.INQUIRY_LABELS]: false,
  [FEATURE_FLAGS.INQUIRY_LEAVE_FEEDBACK]: false,
  [FEATURE_FLAGS.INQUIRY_MULTI_EXPERT_NOTES]: false,
  [FEATURE_FLAGS.INSTITUTIONAL_TRIALS]: false,
  [FEATURE_FLAGS.INQUIRY_ON_YOUR_HONOR]: false,
  [FEATURE_FLAGS.INQUIRY_ON_YOUR_HONOR_REASON]: false,
  [FEATURE_FLAGS.INQUIRY_SUMMARY]: false,
  [FEATURE_FLAGS.INQUIRY_LIBRARY_ANSWERS]: false,
  [FEATURE_FLAGS.LIBRARY_CHAT]: false,
  [FEATURE_FLAGS.MAINTENANCE_MODE]: false,
  [FEATURE_FLAGS.MEDICAL_ON_DEMAND]: false,
  [FEATURE_FLAGS.MICRO_SURVEYS]: false,
  [FEATURE_FLAGS.MOD_DIRECT_TO_MSL]: false,
  [FEATURE_FLAGS.MOD_MIL_ROUTE]: false,
  [FEATURE_FLAGS.MOD_PRODUCT_FEATURED]: false,
  [FEATURE_FLAGS.MOD_PRODUCT_SUPPORT_SUCCESS_SCREEN]: false,
  [FEATURE_FLAGS.MOD_WHEN_TO_ASK]: false,
  [FEATURE_FLAGS.NEW_QUESTION_AFTER_SIGNUP_FUNNEL]: false,
  [FEATURE_FLAGS.ONBOARDING_SURVEY_PREVIEW]: false,
};
